import {
  Add,
  DeleteOutlineRounded,
  EditOutlined,
  KeyboardArrowDownRounded,
  KeyboardArrowUpRounded,
} from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  // Fab,
  Grid,
  SxProps,
  TextField,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { AxiosError } from 'axios'
import dayjs from 'dayjs'
import { nanoid } from 'nanoid'
import { FC, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { UseMutationResult } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { fullName } from '../../../domain/Account/AccountDisplay'
import { getReadableEventType } from '../../../domain/Adopters/AccountDisplay'
import {
  AccountDtoRoles,
  AdoptionAttemptDto,
  AdoptionAttemptEventDto,
  AdoptionAttemptEventDtoType,
  AnimalDto,
} from '../../../interactors/gen/backendClient'
import { useAccountsStore } from '../../../store/AccountsStore'
import { isValidPastDateCoherence } from '../../../utils/date/isValidPastDateCoherence'
import { useDialogOpened } from '../../../utils/hooks/useDialogOpened'
import { useFormExtended } from '../../../utils/hooks/useFormExtended'
import { PATHS } from '../../PATHS'
import { addSecondaryFabSx } from '../../common/addPrimaryCtaSx'
import { ControlledAutoCompleteWithCustomValue } from '../../common/ControlledAutocompleteWithCustomValue'
import { ControlledDateField } from '../../common/ControlledDateField'
import { ControlledSelectField } from '../../common/ControlledSelectField'
import { DisplayChipLikeTextField } from '../../common/DisplayChipLikeTextField'
import { blue, spacingItem, theme } from '../../theme'

interface Props {
  animal: AnimalDto
  attempt: AdoptionAttemptDto
  editAttemptMutation: UseMutationResult<
    AdoptionAttemptDto,
    Error | AxiosError<unknown, unknown>,
    AdoptionAttemptDto,
    unknown
  >
  disabled?: boolean
  sx?: SxProps<Theme>
}

export const AdoptionAttemptEvents: FC<Props> = ({ attempt, editAttemptMutation, sx, animal, disabled = false }) => {
  const [events, setEvents] = useState(attempt.events || [])
  const [expandedEventIndex, setExpandedEventIndex] = useState<number | null>(null)
  const [showAllEvents, setShowAllEvents] = useState(false)
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const initialEventsRef = useRef(attempt.events)

  // Sync events state with attempt.events when attempt.events changes
  useEffect(() => {
    setEvents(attempt.events || [])
    initialEventsRef.current = attempt.events
  }, [attempt.events])

  useEffect(() => {
    const saveOperations = async () => {
      if (JSON.stringify(events) !== JSON.stringify(initialEventsRef.current)) {
        const updatedAttempt = { ...attempt, events: events }

        let done = false
        let paymentDone = false

        events
          .map((event, index) => ({ event, index }))
          .sort((a, b) => {
            const dateA = dayjs(a.event.date, a.event.date?.includes('/') ? 'DD/MM/YYYY' : undefined)
            const dateB = dayjs(b.event.date, b.event.date?.includes('/') ? 'DD/MM/YYYY' : undefined)
            if (dateA.isAfter(dateB)) return 1
            if (dateA.isBefore(dateB)) return -1
            return a.index - b.index
          })
          .map(({ event }) => event)
          .forEach((event: AdoptionAttemptEventDto) => {
            if (event.type === AdoptionAttemptEventDtoType.AdoptionDone) {
              done = true
            }
            if (event.type === AdoptionAttemptEventDtoType.PaymentCancelled) {
              paymentDone = false
            }
            if (
              event.type === AdoptionAttemptEventDtoType.PaymentReception &&
              dayjs(event.date).isBefore(dayjs().add(1, 'day'), 'day')
            ) {
              paymentDone = true
            }
          })

        // Update the values based on the presence of the events
        updatedAttempt.done = done
        updatedAttempt.paymentDone = paymentDone

        await editAttemptMutation.mutateAsync(updatedAttempt)
      }
    }
    saveOperations()
  }, [events])

  return (
    <Card
      sx={{
        ...sx,
        width: '100%',
        borderRadius: 4,
        border: '1px solid rgba(122, 129, 151, 0.18)',
        boxShadow: '0 10px 28px rgba(26, 43, 77, 0.05)',
      }}
    >
      <CardContent
        sx={{
          p: isMobile ? 2 : '16px',
          '&:last-child': { pb: isMobile ? '16px' : '12px' },
        }}
      >
        <Typography sx={{ fontSize: { xs: 18, sm: 20 }, fontWeight: 700, color: '#203864', mb: 1.25 }}>
          Suivi d&apos;adoption
        </Typography>
        <Box pb={0}>
          {events
            .map((event, index) => ({ event, index }))
            .sort((a, b) => {
              const dateA = dayjs(a.event.date, a.event.date?.includes('/') ? 'DD/MM/YYYY' : undefined)
              const dateB = dayjs(b.event.date, b.event.date?.includes('/') ? 'DD/MM/YYYY' : undefined)
              if (dateA.isAfter(dateB)) return -1
              if (dateA.isBefore(dateB)) return 1
              return a.index - b.index
            })
            .map(({ event }) => event)
            .slice(0, showAllEvents ? undefined : 2)
            .map((event, index) => (
              <AdoptionAttemptEventOperation
                key={index}
                animal={animal}
                event={event}
                index={index}
                events={events}
                setEvents={setEvents}
                eventIndex={index}
                expandedEventIndex={expandedEventIndex}
                setExpandedEventIndex={setExpandedEventIndex}
                disabled={disabled}
              />
            ))}
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: isMobile ? 0.5 : 2,
            gap: 1,
          }}
        >
          {events.length > 2 ? (
            <Button
              onClick={() => setShowAllEvents(!showAllEvents)}
              sx={{
                textTransform: 'none',
                color: '#7A8197',
                fontWeight: 600,
                fontSize: 13,
                px: 0.75,
                minHeight: 30,
                minWidth: 88,
                borderRadius: '999px',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                '&:hover': { backgroundColor: 'rgba(32, 56, 100, 0.04)' },
              }}
              color="primary"
            >
              {showAllEvents ? 'Voir moins' : 'Voir plus'}
            </Button>
          ) : (
            <Box />
          )}
          <Box sx={{ minWidth: 0, flexShrink: 1 }}>
            <AdoptionAttemptNewEventModal
              animal={animal}
              events={events}
              setEvents={setEvents}
              setExpandedEventIndex={setExpandedEventIndex}
              disabled={disabled}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

interface PropsItem {
  animal: AnimalDto
  event: AdoptionAttemptEventDto
  index: number
  events: Array<AdoptionAttemptEventDto>
  setEvents: (events: Array<AdoptionAttemptEventDto>) => void
  eventIndex: number
  expandedEventIndex: number | null
  setExpandedEventIndex: React.Dispatch<React.SetStateAction<number | null>>
  disabled?: boolean
}

export const AdoptionAttemptEventOperation: FC<PropsItem> = ({
  event,
  events,
  setEvents,
  index,
  animal,
  eventIndex,
  expandedEventIndex,
  setExpandedEventIndex,
  disabled = false,
}) => {
  const [isExpanded, setExpanded] = useState(false)
  const isUpSmWidth = useMediaQuery(theme.breakpoints.up('sm'))
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const hideMemberOnFirstLine = useMediaQuery('(max-width:750px)')
  const isStripePaymentReception =
    event.type === AdoptionAttemptEventDtoType.PaymentReception &&
    Boolean(
      event.comment?.includes('reçu par Petso') ||
        event.comment?.includes('reçu via votre espace Stripe Connect') ||
        event.comment?.includes('Rendez-vous dans votre suivi des règlements')
    )

  const handleClickOnExpand = () => {
    setExpanded(!isExpanded)
    !isExpanded && setExpandedEventIndex(eventIndex)
  }
  const handleClickOnDelete = () => {
    setEvents(events.filter((_, anIndex) => index !== anIndex))
  }
  useEffect(() => {
    if (eventIndex !== expandedEventIndex) {
      setExpanded(false)
    }
  })

  const sxField = {
    ...spacingItem,
    cursor: 'disabled',
    input: { cursor: 'not-allowed' },
  }

  const accountsStore = useAccountsStore()

  const memberInCharge = accountsStore.members.find((account) => account.id === event.memberInChargeId)

  return (
    <Box
      sx={{
        py: { xs: 1, sm: 1.25 },
        borderBottom: '1px solid rgba(122, 129, 151, 0.16)',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      {isUpSmWidth ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '180px minmax(0, 2fr) 124px',
            columnGap: 4,
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', minHeight: 42 }}>
            <DisplayChipLikeTextField
              label=""
              value={getReadableEventType(event.type)}
              color={blue}
              compact
              borderless
              chipHeight={32}
              emphasis
              maxChipWidth={180}
            />
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: hideMemberOnFirstLine
                ? 'minmax(120px, 0.9fr)'
                : 'minmax(120px, 0.8fr) minmax(220px, 1fr)',
              columnGap: 4,
              alignItems: 'center',
              minWidth: 0,
            }}
          >
            <Box
              sx={{ minWidth: 0, minHeight: 42, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
            >
              <Typography
                variant="caption"
                sx={{ color: '#7A8197', fontSize: { xs: 10.5, sm: 11 }, fontWeight: 500, lineHeight: 1.2, mb: 0.25 }}
              >
                Date
              </Typography>
              <Typography sx={{ color: '#203864', fontSize: { xs: 13, sm: 13.5 }, fontWeight: 400, lineHeight: 1.25 }}>
                {event.date
                  ? dayjs(event.date, event.date.includes('/') ? 'DD/MM/YYYY' : undefined).format('DD/MM/YYYY')
                  : 'N/A'}
              </Typography>
            </Box>
            {!hideMemberOnFirstLine && (
              <Box
                sx={{
                  minWidth: 0,
                  minHeight: 42,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: '#7A8197',
                    fontSize: { xs: 10.5, sm: 11 },
                    fontWeight: 500,
                    lineHeight: 1.2,
                    mb: 0.25,
                  }}
                >
                  Membre en charge
                </Typography>
                <Typography
                  sx={{
                    color: memberInCharge ? '#203864' : '#9AA3B2',
                    fontSize: { xs: 13, sm: 13.5 },
                    fontWeight: 400,
                    lineHeight: 1.25,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {memberInCharge ? fullName(memberInCharge) : '-'}
                </Typography>
              </Box>
            )}
          </Box>
          <Box display="flex" alignItems="center" justifyContent="flex-end" sx={{ width: 124, gap: 0.5 }}>
            <Button
              onClick={handleClickOnExpand}
              sx={{
                minWidth: 0,
                width: 32,
                height: 32,
                p: 0,
                borderRadius: 2,
                color: '#F3AC00',
                backgroundColor: 'rgba(243, 172, 0, 0.08)',
                '&:hover': { backgroundColor: 'rgba(243, 172, 0, 0.16)' },
              }}
            >
              {isExpanded ? (
                <KeyboardArrowUpRounded sx={{ fontSize: 18 }} />
              ) : (
                <KeyboardArrowDownRounded sx={{ fontSize: 18 }} />
              )}
            </Button>
            <AdoptionAttemptEditEventModal
              index={index}
              events={events}
              setEvents={setEvents}
              animal={animal}
              event={event}
              disabled={disabled}
            />
            <Button
              onClick={handleClickOnDelete}
              sx={{
                minWidth: 0,
                width: 32,
                height: 32,
                p: 0,
                borderRadius: 2,
                color: '#D63B32',
                backgroundColor: 'rgba(214, 59, 50, 0.08)',
                '&:hover': { backgroundColor: 'rgba(214, 59, 50, 0.14)' },
              }}
              disabled={disabled || isStripePaymentReception}
            >
              <DeleteOutlineRounded sx={{ fontSize: 20 }} />
            </Button>
          </Box>
        </Box>
      ) : (
        <Grid container spacing={{ xs: 1 }} alignItems="center">
          <Grid item xs={6} pt={0}>
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: { xs: 40, sm: 48 } }}>
              <DisplayChipLikeTextField
                label=""
                value={getReadableEventType(event.type)}
                color={blue}
                compact
                borderless
                chipHeight={30}
              />
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: { xs: 40, sm: 48 } }}
            >
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#7A8197', lineHeight: 1.15 }}>Date</Typography>
              <Typography sx={{ fontSize: { xs: 14, sm: 15 }, color: '#203864', fontWeight: 500, lineHeight: 1.28 }}>
                {event.date
                  ? dayjs(event.date, event.date.includes('/') ? 'DD/MM/YYYY' : undefined).format('DD/MM/YYYY')
                  : 'N/A'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={3}>
            <Box
              display="flex"
              alignItems="center"
              sx={{
                minHeight: { xs: 40, sm: 48 },
                width: '100%',
                justifyContent: 'flex-end',
                gap: 0.45,
              }}
            >
              <Button
                onClick={handleClickOnExpand}
                sx={{
                  minWidth: 0,
                  width: 32,
                  height: 32,
                  borderRadius: 2.25,
                  color: '#E1A300',
                  backgroundColor: 'rgba(252, 180, 2, 0.08)',
                  '&:hover': { backgroundColor: 'rgba(252, 180, 2, 0.14)' },
                  '& .MuiSvgIcon-root': { fontSize: 17 },
                }}
              >
                {isExpanded ? <KeyboardArrowUpRounded /> : <KeyboardArrowDownRounded />}
              </Button>
              <AdoptionAttemptEditEventModal
                index={index}
                events={events}
                setEvents={setEvents}
                animal={animal}
                event={event}
                disabled={disabled}
              />
              <Button
                onClick={handleClickOnDelete}
                sx={{
                  minWidth: 0,
                  width: 32,
                  height: 32,
                  borderRadius: 2.25,
                  color: '#D63B32',
                  backgroundColor: 'rgba(214, 59, 50, 0.08)',
                  '&:hover': { backgroundColor: 'rgba(214, 59, 50, 0.14)' },
                  '& .MuiSvgIcon-root': { fontSize: 16 },
                }}
                disabled={disabled || isStripePaymentReception}
              >
                <DeleteOutlineRounded />
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}
      <Collapse in={isExpanded}>
        <Grid container spacing={{ md: 2, xs: 1 }}>
          {isMobile && (
            <Grid item xs={12}>
              <TextField
                label="Membre en charge"
                type="text"
                fullWidth
                value={memberInCharge ? fullName(memberInCharge) : ''}
                sx={sxField}
                size="small"
                inputProps={{ readOnly: true }}
              />
            </Grid>
          )}
          <Grid item sm={12} xs={12} md={9}>
            <TextField
              label="Details"
              type="text"
              fullWidth
              value={event.comment}
              sx={sxField}
              size="small"
              multiline
              inputProps={{ readOnly: true }}
            />
          </Grid>
        </Grid>
      </Collapse>
      <Box sx={{ display: { xs: 'block', md: 'none' }, width: '100%', my: 2 }}>
        <hr />
      </Box>
    </Box>
  )
}

type FormParams = {
  type: string
  date: string // DateString
  comment: string
  memberInChargeId: string
}

interface PropsNewOperation {
  animal: AnimalDto
  events: Array<AdoptionAttemptEventDto>
  setEvents: (events: Array<AdoptionAttemptEventDto>) => void
  setExpandedEventIndex: React.Dispatch<React.SetStateAction<number | null>>
  disabled?: boolean
}

export const AdoptionAttemptNewEventModal: FC<PropsNewOperation> = ({
  animal,
  events,
  setEvents,
  setExpandedEventIndex,
  disabled = false,
}) => {
  const isMobile = useMediaQuery('(max-width:600px)')
  const { isDialogOpen, openDialog, closeDialog } = useDialogOpened()
  const navigate = useNavigate()
  const accountsStore = useAccountsStore()

  // Filter out contract and certificate related events
  const contractCertificateEvents: AdoptionAttemptEventDtoType[] = [
    AdoptionAttemptEventDtoType.SentCertificate,
    AdoptionAttemptEventDtoType.SentContract,
    AdoptionAttemptEventDtoType.ReceivedSignedCertificate,
    AdoptionAttemptEventDtoType.ReceivedSignedContract,
    AdoptionAttemptEventDtoType.RefusedCertificate,
    AdoptionAttemptEventDtoType.RefusedContract,
    AdoptionAttemptEventDtoType.ValidatedCertificate,
    AdoptionAttemptEventDtoType.ValidatedContract,
  ]

  const availableEventTypes = Object.values(AdoptionAttemptEventDtoType).filter(
    (type) => !contractCertificateEvents.includes(type)
  )

  const {
    register,
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormParams>({
    defaultValues: {
      type: '',
      date: '',
      comment: '',
      memberInChargeId: animal.responsibilities?.memberInChargeAccountId,
    },
    mode: 'onChange',
  })

  const onSubmit = (data: FormParams) => {
    const newOperation = {
      id: nanoid(),
      ...data,
      date: dayjs(data.date).toISOString(),
    }
    setEvents([...events, newOperation as AdoptionAttemptEventDto])
    closeDialog()
    setValue('type', '')
    setValue('memberInChargeId', animal.responsibilities?.memberInChargeAccountId || '')
    setValue('date', '')
    setValue('comment', '')
    setExpandedEventIndex(null)
  }

  const memberInCharge = watch('memberInChargeId')
  useEffect(() => {
    if (memberInCharge === 'addAccount') {
      reset({ memberInChargeId: undefined })
      navigate(
        `${PATHS.ajouterBenevole.absolute}?from=animaux/${animal.id}?tab=sante&role=${AccountDtoRoles.Member}&card=animalHealthEventsCard`
      )
    }
  }, [memberInCharge])

  return (
    <>
      <Button
        color="primary"
        sx={[
          addSecondaryFabSx,
          { width: { xs: '100%', sm: 'auto' }, minHeight: 38, height: 38, fontSize: { xs: 14, sm: 15 } },
        ]}
        variant="outlined"
        onClick={openDialog}
        disabled={disabled}
      >
        {!isMobile && <Add sx={{ mr: 1 }} />}
        {/* <Add sx={{ mr: 1 }} /> */}
        Ajouter un suivi
      </Button>
      <Dialog
        open={isDialogOpen}
        onClose={closeDialog}
        PaperProps={{
          style: { borderRadius: 15, padding: 15 },
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Box>
              <Typography variant="h4" component="h2" marginBottom={4}>
                Ajouter un suivi
              </Typography>
              <ControlledSelectField
                sx={{ ...spacingItem, width: '100%' }}
                control={control}
                error={errors.type}
                fieldName="type"
                size="small"
                label="Motif"
                options={availableEventTypes.map((status) => ({
                  label: getReadableEventType(status as AdoptionAttemptEventDtoType),
                  value: status,
                }))}
                requiredRule={'Le type est requis'}
              />

              <ControlledDateField
                control={control}
                validate={(value) => isValidPastDateCoherence(value) || value === null}
                error={errors.date}
                fieldName={'date'}
                label="Date"
                requiredRule={undefined}
                size="small"
                sx={spacingItem}
              />

              <ControlledAutoCompleteWithCustomValue
                control={control}
                fieldName={'memberInChargeId'}
                size="small"
                options={[
                  { value: 'addAccount', label: 'Ajouter un bénévole' },
                  ...accountsStore.members.map((option) => ({
                    label: fullName(option),
                    value: option.id,
                  })),
                ]}
                label="Membre en charge"
                error={errors.memberInChargeId}
                requiredRule={undefined}
                defaultValue={animal.responsibilities?.memberInChargeAccountId || ''}
                sx={spacingItem}
                highlight
              />

              <TextField
                id="additional-info-input"
                label="Détails"
                type="text"
                fullWidth
                {...register('comment')}
                sx={spacingItem}
                size="small"
                multiline
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              data-testid="cancel"
              variant="outlined"
              onClick={closeDialog}
              sx={{
                textTransform: 'none',
                fontSize: isMobile ? '14px' : '16px',
                width: isMobile ? '50%' : 'auto',
                px: isMobile ? 'auto' : '50px',
              }}
            >
              Annuler
            </Button>
            <LoadingButton
              data-testid="apply-changes"
              variant="contained"
              type="submit"
              sx={{
                textTransform: 'none',
                fontSize: isMobile ? '14px' : '16px',
                width: isMobile ? '50%' : 'auto',
                px: isMobile ? 'auto' : '50px',
              }}
              disabled={disabled}
            >
              Valider
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>
    </>
  )
}

interface PropsEditOperation {
  animal: AnimalDto
  events: Array<AdoptionAttemptEventDto>
  event: AdoptionAttemptEventDto
  setEvents: (events: Array<AdoptionAttemptEventDto>) => void
  index: number
  disabled?: boolean
}

export const AdoptionAttemptEditEventModal: FC<PropsEditOperation> = ({
  animal,
  event,
  events,
  setEvents,
  index,
  disabled = false,
}) => {
  const queryParams = new URLSearchParams(location.search)
  const navigate = useNavigate()
  const { isDialogOpen, openDialog, closeDialog } = useDialogOpened()
  const accountsStore = useAccountsStore()
  const isMobile = useMediaQuery('(max-width:600px)')

  // Filter out contract and certificate related events
  const contractCertificateEvents: AdoptionAttemptEventDtoType[] = [
    AdoptionAttemptEventDtoType.SentCertificate,
    AdoptionAttemptEventDtoType.SentContract,
    AdoptionAttemptEventDtoType.ReceivedSignedCertificate,
    AdoptionAttemptEventDtoType.ReceivedSignedContract,
    AdoptionAttemptEventDtoType.RefusedCertificate,
    AdoptionAttemptEventDtoType.RefusedContract,
    AdoptionAttemptEventDtoType.ValidatedCertificate,
    AdoptionAttemptEventDtoType.ValidatedContract,
  ]

  const availableEventTypes = Object.values(AdoptionAttemptEventDtoType).filter(
    (type) => !contractCertificateEvents.includes(type)
  )

  useEffect(() => {
    if (queryParams.get('card') === JSON.stringify(event)) {
      openDialog()
    }
  }, [])

  const getDefaultValues = (event: AdoptionAttemptEventDto): FormParams => ({
    type: event.type,
    date: event.date || '',
    comment: event.comment || '',
    memberInChargeId: queryParams.get('memberInCharge') || event.memberInChargeId || '',
  })

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = useFormExtended(event, getDefaultValues)

  const onSubmit = (data: FormParams) => {
    const newOperation = {
      id: nanoid(),
      ...data,
      date: dayjs(data.date).toISOString(),
    }

    // create a copy of events
    const updatedOperations = [...events]

    // replace the event at the given index
    updatedOperations[index] = newOperation as AdoptionAttemptEventDto

    // update the state
    setEvents(updatedOperations)

    closeDialog()
  }

  const memberInCharge = watch('memberInChargeId')
  useEffect(() => {
    if (memberInCharge === 'addAccount') {
      reset({ memberInChargeId: undefined })
      navigate(
        `${PATHS.ajouterBenevole.absolute}?from=animaux/${animal.id}?tab=sante&role=${
          AccountDtoRoles.Member
        }&card=${JSON.stringify(event)}`
      )
    }
  }, [memberInCharge])

  return (
    <>
      <Button
        onClick={openDialog}
        sx={{
          minWidth: 0,
          width: { xs: 34, sm: 42 },
          height: { xs: 34, sm: 42 },
          borderRadius: 2,
          color: '#203864',
          backgroundColor: 'rgba(32, 56, 100, 0.08)',
          '&:hover': { backgroundColor: 'rgba(32, 56, 100, 0.14)' },
          '& .MuiSvgIcon-root': { fontSize: { xs: 18, sm: 24 } },
        }}
        disabled={
          disabled ||
          event.comment?.includes('reçu par Petso. Contactez nous pour récupérer le versement. (contact@petso.fr)')
        }
      >
        <EditOutlined />
      </Button>
      <Dialog
        open={isDialogOpen}
        onClose={closeDialog}
        PaperProps={{
          style: { borderRadius: 15, padding: 15 },
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Box>
              <Typography variant="h4" component="h2" marginBottom={4}>
                Modifier l&#39;injection
              </Typography>
              <ControlledSelectField
                sx={{ ...spacingItem, width: '100%' }}
                control={control}
                error={errors.type}
                fieldName="type"
                size="small"
                label="Motif"
                options={availableEventTypes.map((status) => ({
                  label: getReadableEventType(status as AdoptionAttemptEventDtoType),
                  value: status,
                }))}
                requiredRule={'Le motif est requis'}
              />

              <ControlledDateField
                control={control}
                validate={(value) => isValidPastDateCoherence(value) || value === null}
                error={errors.date}
                fieldName={'date'}
                label="Date"
                requiredRule={undefined}
                size="small"
                sx={spacingItem}
              />

              <ControlledAutoCompleteWithCustomValue
                control={control}
                fieldName={'memberInChargeId'}
                size="small"
                options={[
                  { value: 'addAccount', label: 'Ajouter un bénévole' },
                  ...accountsStore.members.map((option) => ({
                    label: fullName(option),
                    value: option.id,
                  })),
                ]}
                label="Membre en charge"
                error={errors.memberInChargeId}
                requiredRule={undefined}
                defaultValue={animal.responsibilities?.memberInChargeAccountId || ''}
                sx={spacingItem}
                highlight
              />

              <TextField
                id="additional-info-input"
                label="Détails"
                type="text"
                fullWidth
                {...register('comment')}
                sx={spacingItem}
                size="small"
                multiline
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              data-testid="cancel"
              variant="outlined"
              onClick={closeDialog}
              sx={{
                textTransform: 'none',
                fontSize: isMobile ? '14px' : '16px',
                width: isMobile ? '50%' : 'auto',
                px: isMobile ? 'auto' : '50px',
              }}
            >
              Annuler
            </Button>
            <LoadingButton
              data-testid="apply-changes"
              variant="contained"
              type="submit"
              sx={{
                textTransform: 'none',
                fontSize: isMobile ? '14px' : '16px',
                width: isMobile ? '50%' : 'auto',
                px: isMobile ? 'auto' : '50px',
              }}
            >
              Modifier
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>
    </>
  )
}
